import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { SmartMovingConfig, SyncLogEntry } from '../types/admin';

interface ConfigResponse {
  success: boolean;
  data: SmartMovingConfig | null;
}

interface SyncLogResponse {
  success: boolean;
  data: SyncLogEntry[];
}

interface SmartMovingTestSampleData {
  leadsEndpoint: string;
  leadsCount: number;
  leads: Array<Record<string, unknown>>;
  jobsEndpoint: string;
  jobsCount: number;
  jobs: Array<Record<string, unknown>>;
}

interface TestResult {
  success: boolean;
  message: string;
  sandbox?: boolean;
  sampleData?: SmartMovingTestSampleData;
}

interface TestResponse {
  success: boolean;
  data: TestResult;
}

interface SyncResult {
  imported: number;
  skipped: number;
  errors: number;
  details: string[];
}

interface SyncResponse {
  success: boolean;
  data: SyncResult;
}

interface PushResult {
  success: boolean;
  smartMovingLeadId?: string;
  message: string;
}

interface PushResponse {
  success: boolean;
  data: PushResult;
}

export function useSmartMovingIntegration(orgId: string) {
  const [config, setConfig] = useState<SmartMovingConfig | null>(null);
  const [syncLog, setSyncLog] = useState<SyncLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<ConfigResponse>(`/org-admin/${orgId}/integrations/smartmoving`);
      setConfig(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load SmartMoving config');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  const fetchSyncLog = useCallback(async () => {
    try {
      const res = await api.get<SyncLogResponse>(`/org-admin/${orgId}/integrations/smartmoving/sync-log`);
      setSyncLog(res.data);
    } catch {
      // Non-critical — don't set error state
    }
  }, [orgId]);

  useEffect(() => {
    fetchConfig();
    fetchSyncLog();
  }, [fetchConfig, fetchSyncLog]);

  const saveConfig = useCallback(
    async (data: Partial<SmartMovingConfig> & { apiKey: string }) => {
      const res = await api.put<ConfigResponse>(`/org-admin/${orgId}/integrations/smartmoving`, data);
      setConfig(res.data);
      return res.data;
    },
    [orgId],
  );

  const testConnection = useCallback(
    async (apiKey?: string, baseUrl?: string) => {
      const body: Record<string, string> = {};
      if (apiKey) body.apiKey = apiKey;
      if (baseUrl) body.baseUrl = baseUrl;
      const res = await api.post<TestResponse>(`/org-admin/${orgId}/integrations/smartmoving/test`, body);
      return res.data;
    },
    [orgId],
  );

  const triggerSync = useCallback(async (): Promise<SyncResult> => {
    const res = await api.post<SyncResponse>(`/org-admin/${orgId}/integrations/smartmoving/sync`, {});
    fetchConfig();
    fetchSyncLog();
    return res.data;
  }, [orgId, fetchConfig, fetchSyncLog]);

  const pushQuote = useCallback(async (quoteId: string): Promise<PushResult> => {
    const res = await api.post<PushResponse>(
      `/org-admin/${orgId}/integrations/smartmoving/push/${quoteId}`,
      {},
    );
    fetchSyncLog();
    return res.data;
  }, [orgId, fetchSyncLog]);

  const disconnect = useCallback(async () => {
    await api.del(`/org-admin/${orgId}/integrations/smartmoving`);
    setConfig(null);
    setSyncLog([]);
  }, [orgId]);

  return {
    config,
    syncLog,
    loading,
    error,
    saveConfig,
    testConnection,
    triggerSync,
    pushQuote,
    disconnect,
    refetch: fetchConfig,
    refetchSyncLog: fetchSyncLog,
  };
}
